import React, { useEffect, useMemo, useRef } from 'react'
import { remark } from 'remark'
import strip from 'strip-markdown'

import { useMeta, useTitle } from 'hoofd'

import { DEFAULT_BACKGROUND } from '@data/config/images'

/**
 * Safe replacement for hoofd's useLink that guards the removeChild cleanup.
 * hoofd's built-in useLink crashes with "The node to be removed is not a child
 * of this node" when multiple MetadataCanonicalUrl components mount/unmount
 * during navigation (e.g. GuildNavigator + GuildSettingsMyMembershipScreen
 * both render canonical URLs).
 */
const useSafeLink = (options: Record<string, string>) => {
  const node = useRef<HTMLLinkElement | undefined>(undefined)

  useEffect(() => {
    // Try to find an existing matching element (e.g. SSR-rendered)
    const selector = Object.entries(options)
      .map(([key, value]) => `[${key}="${CSS.escape(value)}"]`)
      .join('')
    const existing = document.head.querySelector<HTMLLinkElement>(
      `link${selector}`
    )

    if (existing) {
      node.current = existing
    } else {
      const el = document.createElement('link')
      Object.entries(options).forEach(([key, value]) => {
        el.setAttribute(key, value)
      })
      document.head.appendChild(el)
      node.current = el
    }

    return () => {
      if (node.current && node.current.parentNode === document.head) {
        document.head.removeChild(node.current)
      }
      node.current = undefined
    }
  }, [])
}

const IMAGE_TRANSFORM_PARAMS = {
  DEFAULT: {
    'og:image': '?w=1200',
    'twitter:image': '?w=1200&h=600&fit=cover',
  },
  FULL_SIZE: {
    'og:image': '?w=1200',
    'twitter:image': '?w=1200&h=600&fit=contain',
  },
  NONE: {
    'og:image': '',
    'twitter:image': '',
  },
} as const

export const Metadata = ({
  canonicalUrl,
  description,
  imageTransformParams = 'DEFAULT',
  imageWidth = 1200,
  imageHeight = 630,
  ogType,
  photoUrl,
  title,
}: {
  canonicalUrl?: string
  description?: string | null
  imageTransformParams?: keyof typeof IMAGE_TRANSFORM_PARAMS
  imageWidth?: number
  imageHeight?: number
  ogType?: string
  photoUrl?: string | null
  title: string
}) => {
  const imageURL = useMemo(() => {
    return photoUrl ?? DEFAULT_BACKGROUND
  }, [photoUrl])

  const contentDescription = useMemo(() => {
    if (!description) {
      return
    }

    // @ts-expect-error strip-markdown types are incompatible with remark
    return String(remark().use(strip).processSync(description))
      // remark-stringify escapes markdown-special characters with backslashes (e.g. \* \_ \[)
      // which appear as literal text in OG meta tags on Discord, Twitter, etc.
      .replace(/\\([\\*_~`\[\](){}#+\-.!|>])/g, '$1')
  }, [description])

  useTitle(`${title} | Guild`)
  useMeta({ property: 'og:title', content: `${title} | Guild` })

  useMeta({
    property: 'og:image',
    content: `${imageURL}${IMAGE_TRANSFORM_PARAMS[imageTransformParams]['og:image']}`,
  })

  useMeta({ property: 'twitter:title', content: `${title} | Guild` })
  useMeta({ property: 'twitter:card', content: 'summary_large_image' })
  useMeta({
    property: 'twitter:image',
    content: `${imageURL}${IMAGE_TRANSFORM_PARAMS[imageTransformParams]['twitter:image']}`,
  })

  useMeta({ property: 'og:image:width', content: String(imageWidth) })
  useMeta({ property: 'og:image:height', content: String(imageHeight) })

  return (
    <>
      {contentDescription ? (
        <MetadataDescription description={contentDescription} />
      ) : null}
      {ogType ? <MetadataOgType ogType={ogType} /> : null}
      {canonicalUrl ? <MetadataCanonicalUrl url={canonicalUrl} /> : null}
    </>
  )
}

const MetadataOgType = ({ ogType }: { ogType: string }) => {
  useMeta({ property: 'og:type', content: ogType })
  return null
}

const MetadataCanonicalUrl = ({ url }: { url: string }) => {
  useMeta({ property: 'og:url', content: url })
  useSafeLink({ rel: 'canonical', href: url })
  return null
}

const MetadataDescription = ({ description }: { description: string }) => {
  const shortenedDescription = description.split('\n').slice(0, 4).join('\n')

  useMeta({
    name: 'description',
    content: shortenedDescription,
  })
  useMeta({
    property: 'og:description',
    content: description.substring(0, 300),
  })
  useMeta({ property: 'twitter:description', content: shortenedDescription })

  return null
}
